import React, { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import useFocusTrap from '../hooks/useFocusTrap';
import { useUser } from '../contexts/UserContext';
import { createOrLinkCustomer, customerTypeColumns, displayNameFromForm, nameOrNull } from '../utils/customerRelationshipUtils';
import { useCustomerTags } from '../hooks/useCustomerPageData';
import { supabase } from '../utils/supabaseClient';
import { showToast } from './Common/Toast';
import { StateSelect } from '@tradesmatepro/shared-ui';
import PhoneField from './Common/PhoneField';
import { validateCustomerFields } from '../utils/customerValidation';

// Defaults match the formData shape in pages/Customers.js so records created
// here look identical to ones created from the main customer page.
const DEFAULT_FORM_DATA = {
  type: 'residential',
  first_name: '',
  last_name: '',
  company_name: '',
  email: '',
  phone: '',
  invite_to_portal: false,
  street_address: '',
  city: '',
  state: '',
  billing_address_line_1: '',
  billing_address_line_2: '',
  billing_city: '',
  billing_state: '',
  billing_zip_code: '',
  status: 'active',
  tags: [],
  notes: ''
};

export default function AddCustomerModal({ open, onClose, onCreated }) {
  const { user } = useUser();
  const { data: customerTags = [] } = useCustomerTags(user?.company_id);
  const [formData, setFormData] = useState(DEFAULT_FORM_DATA);
  const [submitting, setSubmitting] = useState(false);
  // Field-level errors, same shape and same source of truth as the Customers page.
  const [errors, setErrors] = useState({});
  // Trap Tab within the dialog (WCAG 2.1 SC 2.1.2) — this hand-rolled modal never inherited
  // GlassModal's focus trap, so Tab escaped to the page behind it (finding #75). `open` gates
  // the trap because this component stays mounted and returns null when closed.
  const trapRef = useFocusTrap(open);

  if (!open) return null;

  const resetForm = () => {
    setFormData(DEFAULT_FORM_DATA);
    setErrors({});
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Mirrors handleCreateCustomer in pages/Customers.js — same persistence path
  // (createOrLinkCustomer), same tag assignment, same portal invite flow.
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    // Board #465. This path had NO validation, so both rules failed the way they fail when
    // nobody checks: a bad email reached the CHECK constraint as a raw 23514, and a phone
    // with letters was silently nulled by the format_phone_e164 trigger while the modal
    // still reported the customer created. Same validator as the Customers page.
    const fieldErrors = validateCustomerFields(formData);
    if (Object.keys(fieldErrors).length > 0) {
      setErrors(fieldErrors);
      return;
    }
    setErrors({});

    setSubmitting(true);
    try {
      const customerName = displayNameFromForm(formData);

      const result = await createOrLinkCustomer(user?.company_id, {
        name: customerName,
        // Both type columns. This path wrote `type` only and never customer_type, which is
        // the column the Customers table badge reads — so a customer added here had a NULL
        // badge from birth (board #262).
        ...customerTypeColumns(formData),
        // Both families, always. Gating these on `type` discarded whichever one the current
        // type did not claim — the same defect as board #262 on the Customers page.
        first_name: nameOrNull(formData.first_name),
        last_name: nameOrNull(formData.last_name),
        company_name: nameOrNull(formData.company_name),
        email: formData.email,
        phone: formData.phone,
        notes: formData.notes,
        billing_address_line_1: formData.billing_address_line_1,
        billing_address_line_2: formData.billing_address_line_2,
        billing_city: formData.billing_city,
        billing_state: formData.billing_state,
        billing_zip_code: formData.billing_zip_code,
        billing_country: 'United States',
        created_via: 'internal'
      }, user?.id);

      if (!result.success) {
        throw new Error(result.error || 'Failed to create customer');
      }

      const customer = result.customer;

      // Tag assignment (non-blocking — same pattern as Customers.js). The contractor's own tags
      // only: customer type is its own field, not a tag (C-12, 2026-10-17).
      if (customer && formData.tags && formData.tags.length > 0) {
        try {
          const tagsToAssign = [];
          if (formData.tags && formData.tags.length > 0) {
            tagsToAssign.push(...formData.tags.map(t => t.toLowerCase()));
          }
          const uniqueTags = [...new Set(tagsToAssign)];

          for (const tagName of uniqueTags) {
            // maybeSingle() returns null for 0 rows without 406-ing
            const { data: tagData } = await supabase
              .from('customer_tags')
              .select('*')
              .eq('name', tagName)
              .eq('company_id', user.company_id)
              .maybeSingle();

            let tagId = tagData?.id;
            if (!tagId) {
              const { data: createdTag, error: createError } = await supabase
                .from('customer_tags')
                .insert({
                  company_id: user.company_id,
                  name: tagName,
                  description: `${tagName.charAt(0).toUpperCase() + tagName.slice(1)} customer tag`,
                  color: tagName === 'commercial' ? '#3b82f6'
                       : tagName === 'industrial' ? '#f59e0b'
                       : tagName === 'vip' ? '#ef4444' : '#10b981'
                })
                .select()
                .single();
              if (!createError && createdTag) tagId = createdTag.id;
            }

            if (tagId) {
              await supabase
                .from('customer_tag_assignments')
                .insert({
                  customer_id: customer.id,
                  tag_id: tagId,
                  company_id: user.company_id,
                  assigned_by: user.id
                });
            }
          }
        } catch (tagError) {
          console.warn('Failed to add customer tags (non-blocking):', tagError);
        }
      }

      // Portal invite — same flow as Customers.js
      if (formData.invite_to_portal && formData.email) {
        try {
          const { CustomerAuthService } = await import('../services/CustomerAuthService');
          await CustomerAuthService.addCustomerWithPortalAccount({
            name: customerName,
            email: formData.email,
            phone: formData.phone,
            street_address: formData.billing_address_line_1,
            city: formData.billing_city,
            state: formData.billing_state,
            zip_code: formData.billing_zip_code
          }, user.company_id, user.id);
        } catch (e) {
          console.warn('Portal invite failed (non-blocking):', e);
        }
      }

      showToast('success', 'Customer created successfully!');
      if (onCreated) onCreated(customer);
      resetForm();
      onClose();
    } catch (error) {
      console.error('Error creating customer:', error);
      showToast('error', error.message || 'Failed to create customer');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      data-testid="customer-form-modal"
    >
      <div
        ref={trapRef}
        role="dialog"
        aria-modal="true"
        className="bg-gradient-to-br from-white/80 dark:from-slate-900/80 to-blue-50/40 dark:to-blue-900/20 backdrop-blur-xl rounded-lg border border-blue-200/50 dark:border-blue-900/50 p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Add New Customer</h3>
          <button
            type="button"
            onClick={handleClose}
            disabled={submitting}
            data-testid="customer-form-close"
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Customer Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Customer Type *
            </label>
            <div className="grid grid-cols-3 gap-3">
              {['residential', 'commercial', 'industrial'].map((type) => (
                <label
                  key={type}
                  className={`flex items-center space-x-2 p-3 border-2 rounded-lg cursor-pointer transition-all ${
                    formData.type === type
                      ? 'border-primary-500 text-primary-700 dark:text-primary-400'
                      : 'border-gray-300 dark:border-slate-700/40 text-gray-700 dark:text-gray-300 hover:bg-slate-50 dark:hover:bg-slate-800/40'
                  }`}
                >
                  <input
                    type="radio"
                    name="type"
                    value={type}
                    checked={formData.type === type}
                    onChange={(e) => setFormData({
                      ...formData,
                      type: e.target.value
                      // Name fields are deliberately NOT cleared — switching type used to
                      // wipe whatever the user had already typed (board #262).
                    })}
                    data-testid={`type-${type}`}
                    className="text-primary-600"
                  />
                  <span className="text-sm font-medium capitalize">{type}</span>
                </label>
              ))}
            </div>
          </div>

          {/* BOTH name blocks, always. Type decides which is REQUIRED, never which exists —
              same rule as the Customers page (board #262). Swapping the schema here hid a
              value the user had already typed while still holding it in form state, which
              would now save a name nobody could see. */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="customer-first-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                First Name{formData.type === 'residential' ? ' *' : ''}
              </label>
              <input
                id="customer-first-name"
                name="first_name"
                type="text"
                required={formData.type === 'residential'}
                autoFocus={formData.type === 'residential'}
                maxLength={100}
                value={formData.first_name}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                placeholder="John"
                aria-invalid={errors.first_name ? 'true' : undefined}
                data-testid="input-first-name"
                className="w-full px-3 py-2 rounded-lg bg-white/70 dark:bg-slate-800/60 border border-white/20 dark:border-slate-700/40 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              {errors.first_name ? (
                <p className="text-xs text-red-600 dark:text-red-400 mt-1">{errors.first_name}</p>
              ) : null}
            </div>
            <div>
              <label htmlFor="customer-last-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Last Name{formData.type === 'residential' ? ' *' : ''}
              </label>
              <input
                id="customer-last-name"
                name="last_name"
                type="text"
                required={formData.type === 'residential'}
                maxLength={100}
                value={formData.last_name}
                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                placeholder="Smith"
                data-testid="input-last-name"
                className="w-full px-3 py-2 rounded-lg bg-white/70 dark:bg-slate-800/60 border border-white/20 dark:border-slate-700/40 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              {errors.last_name ? (
                <p className="text-xs text-red-600 dark:text-red-400 mt-1">{errors.last_name}</p>
              ) : null}
            </div>
          </div>

          <div>
            <label htmlFor="customer-company-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Business Name{formData.type === 'residential' ? '' : ' *'}
            </label>
            <input
              id="customer-company-name"
              name="company_name"
              type="text"
              required={formData.type !== 'residential'}
              autoFocus={formData.type !== 'residential'}
              maxLength={200}
              value={formData.company_name}
              onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
              placeholder="ABC Plumbing Inc."
              aria-invalid={errors.company_name ? 'true' : undefined}
              data-testid="input-company-name"
              className="w-full px-3 py-2 rounded-lg bg-white/70 dark:bg-slate-800/60 border border-white/20 dark:border-slate-700/40 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            {errors.company_name ? (
              <p className="text-xs text-red-600 dark:text-red-400 mt-1">{errors.company_name}</p>
            ) : null}
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {formData.type === 'residential'
                ? 'Optional. If you fill this in, it becomes the name shown on quotes and invoices.'
                : 'Shown on quotes and invoices. First and last name are the contact person, if you have one.'}
            </p>
          </div>

          {/* Contact info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="customer-email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Email
              </label>
              <input
                id="customer-email"
                name="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="customer@example.com"
                data-testid="input-email"
                aria-invalid={errors.email ? 'true' : undefined}
                className="w-full px-3 py-2 rounded-lg bg-white/70 dark:bg-slate-800/60 border border-white/20 dark:border-slate-700/40 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              {errors.email ? (
                <p className="text-xs text-red-600 dark:text-red-400 mt-1">{errors.email}</p>
              ) : null}
            </div>
            <div>
              {/* Board #465. This was a hand-rolled picker offering THIRTEEN calling codes next
                  to a free-text box — so a contractor in any other country had no code to pick,
                  and whatever they typed went to the server unchecked. Common/PhoneField is the
                  one control (libphonenumber-js, every country, as-you-type formatting) and has
                  already been broken and repaired three times; adopting it is what stops this
                  surface from inheriting a fourth. */}
              <PhoneField
                label="Phone"
                name="phone"
                testId="input-phone"
                value={formData.phone}
                onChange={(next) => setFormData({ ...formData, phone: next })}
                error={errors.phone}
              />
            </div>
          </div>

          {/* Status */}
          <div>
            <label htmlFor="customer-status" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Status
            </label>
            <select
              id="customer-status"
              name="status"
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              data-testid="input-status"
              className="w-full px-3 py-2 rounded-lg bg-white/70 dark:bg-slate-800/60 border border-white/20 dark:border-slate-700/40 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          {/* Billing Address */}
          <div className="border-t pt-4">
            <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-3">Billing Address</h4>
            <div className="space-y-4">
              <div>
                <label htmlFor="customer-street" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Street Address
                </label>
                <input
                  id="customer-street"
                  name="street_address"
                  type="text"
                  value={formData.billing_address_line_1}
                  onChange={(e) => setFormData({
                    ...formData,
                    billing_address_line_1: e.target.value,
                    street_address: e.target.value
                  })}
                  placeholder="123 Main St"
                  data-testid="input-street-address"
                  className="w-full px-3 py-2 rounded-lg bg-white/70 dark:bg-slate-800/60 border border-white/20 dark:border-slate-700/40 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label htmlFor="customer-street2" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Address Line 2 (Optional)
                </label>
                <input
                  id="customer-street2"
                  name="address_line_2"
                  type="text"
                  value={formData.billing_address_line_2}
                  onChange={(e) => setFormData({ ...formData, billing_address_line_2: e.target.value })}
                  placeholder="Apt, Suite, Unit, etc."
                  className="w-full px-3 py-2 rounded-lg bg-white/70 dark:bg-slate-800/60 border border-white/20 dark:border-slate-700/40 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label htmlFor="customer-city" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    City
                  </label>
                  <input
                    id="customer-city"
                    name="city"
                    type="text"
                    value={formData.billing_city}
                    onChange={(e) => setFormData({
                      ...formData,
                      billing_city: e.target.value,
                      city: e.target.value
                    })}
                    placeholder="The Dalles"
                    data-testid="input-city"
                    className="w-full px-3 py-2 rounded-lg bg-white/70 dark:bg-slate-800/60 border border-white/20 dark:border-slate-700/40 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label htmlFor="customer-state" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    State
                  </label>
                  <StateSelect
                    id="customer-state"
                    name="state"
                    value={formData.billing_state}
                    onChange={(e) => setFormData({
                      ...formData,
                      billing_state: e.target.value,
                      state: e.target.value
                    })}
                    data-testid="input-state"
                    className="w-full px-3 py-2 rounded-lg bg-white/70 dark:bg-slate-800/60 border border-white/20 dark:border-slate-700/40 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label htmlFor="customer-zip" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    ZIP Code
                  </label>
                  <input
                    id="customer-zip"
                    name="billing_zip_code"
                    type="text"
                    value={formData.billing_zip_code}
                    onChange={(e) => setFormData({
                      ...formData,
                      billing_zip_code: e.target.value
                    })}
                    placeholder="97058"
                    data-testid="input-zip-code"
                    className="w-full px-3 py-2 rounded-lg bg-white/70 dark:bg-slate-800/60 border border-white/20 dark:border-slate-700/40 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Tags */}
          {customerTags.length > 0 && (
            <div className="border-t pt-4">
              <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-3">Customer Tags</h4>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Available Tags
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {customerTags.map(tag => {
                      const selected = (formData.tags || []).includes(tag.name);
                      return (
                        <button
                          key={tag.id}
                          type="button"
                          onClick={() => {
                            const currentTags = formData.tags || [];
                            const newTags = selected
                              ? currentTags.filter(t => t !== tag.name)
                              : [...currentTags, tag.name];
                            setFormData({ ...formData, tags: newTags });
                          }}
                          className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                            selected
                              ? 'bg-primary-100 text-primary-800 border-2 border-primary-300'
                              : 'bg-slate-100 dark:bg-slate-700/40 text-slate-700 dark:text-slate-300 border-2 border-transparent hover:bg-slate-200 dark:hover:bg-slate-700/60'
                          }`}
                          style={{ backgroundColor: selected ? tag.color + '20' : undefined }}
                        >
                          {tag.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Portal invite */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="customer-invite-to-portal"
              name="invite_to_portal"
              checked={formData.invite_to_portal}
              onChange={(e) => setFormData({ ...formData, invite_to_portal: e.target.checked })}
              data-testid="input-invite-portal"
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label htmlFor="customer-invite-to-portal" className="ml-2 block text-sm text-gray-900 dark:text-gray-200">
              Invite to Customer Portal
              {formData.email ? (
                <span className="text-gray-500 text-xs block">
                  Will send magic link to {formData.email}
                </span>
              ) : (
                <span className="text-gray-500 text-xs block">
                  Email address required for portal invitation
                </span>
              )}
            </label>
          </div>

          {/* Notes */}
          <div>
            <label htmlFor="customer-notes" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Notes
            </label>
            <textarea
              id="customer-notes"
              name="notes"
              rows={3}
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Optional notes about this customer"
              data-testid="input-notes"
              className="w-full px-3 py-2 rounded-lg bg-white/70 dark:bg-slate-800/60 border border-white/20 dark:border-slate-700/40 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              disabled={submitting}
              data-testid="customer-form-cancel"
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              data-testid="customer-form-submit"
              className="btn-primary"
            >
              {submitting ? 'Adding...' : 'Add Customer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
